<template>
  <div class="score-result">
    <span class="m600 sec-label" tabindex="-1" data-scorer-focus>{{ copy.eyebrow }}</span>

    <div class="score-result__profile">
      <div
        v-for="d in domains"
        :key="d.id"
        class="score-result__chip"
        :class="{ 'score-result__chip--floor': summary.levels[d.id] === summary.floor }"
      >
        <span class="o700 score-result__lv sec-num">{{ summary.levels[d.id] }}</span>
        <span class="m500 score-result__lb">{{ d.name }}</span>
      </div>
    </div>

    <div class="score-result__scores">
      <div class="score-result__big">
        <span class="o700 score-result__v sec-num">{{ summary.average }}</span>
        <span class="m700 score-result__l">{{ copy.average }}</span>
      </div>
      <div class="score-result__big">
        <span class="o700 score-result__v score-result__v--floor sec-num">{{ summary.floor }}</span>
        <span class="m700 score-result__l">{{ copy.floor }}</span>
      </div>
    </div>

    <p class="o600 score-result__floorline">{{ line.before }}<em>{{ line.em }}</em>{{ line.after }}</p>
    <p class="m500 sec-body">{{ copy.actOn }}</p>
    <p class="m500 sec-body">{{ summary.verdict }}</p>
    <p class="m500 score-result__hint">{{ copy.indicative }}</p>

    <sec-actions :actions="copy.actions" />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import SecActions from '@/components/security/ui/SecActions.vue';
import { DOMAINS } from '@/content/security/domains';
import { SCORER_COPY, scoreSummary } from '@/content/security/score';

const props = defineProps({
  answers: { type: Array, required: true },
});

const copy = SCORER_COPY.result;
const domains = DOMAINS;
const summary = computed(() => scoreSummary(props.answers));
const line = computed(() => copy.floorLine(summary.value.floor, summary.value.lowestNames));
</script>

<style scoped>
.score-result {
  display: flex;
  flex-direction: column;
  gap: var(--sec-s3);
}

.score-result__profile {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: var(--sec-s2);
}

.score-result__chip {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: var(--sec-s3);
  background: var(--sec-surface);
  border: 1px solid var(--sec-line);
  border-radius: var(--sec-radius-card);
}

.score-result__chip--floor {
  border-color: var(--sec-accent);
  box-shadow: inset 3px 0 0 var(--sec-accent);
}

.score-result__lv {
  font-size: 1.9rem;
  line-height: 1;
  color: var(--sec-ink);
}

.score-result__lb {
  font-size: 0.85rem;
  line-height: 130%;
  color: var(--sec-ink-2);
}

.score-result__scores {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sec-s4);
}

.score-result__big {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.score-result__v {
  font-size: 2.25rem;
  line-height: 1;
  color: var(--sec-ink);
}

.score-result__v--floor {
  color: var(--sec-accent-text);
}

.score-result__l {
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--sec-ink-3);
}

.score-result__floorline {
  font-size: 1.5rem;
  line-height: 1.2;
  color: var(--sec-ink);
  text-wrap: balance;
}

.score-result__floorline em {
  font-style: normal;
  color: var(--sec-accent-text);
}

.score-result__hint {
  font-size: 0.88rem;
  color: var(--sec-ink-3);
  max-width: var(--sec-measure);
}

@media only screen and (min-width: 700px) {
  .score-result__floorline {
    font-size: 2.1rem;
  }
}
</style>
